import React, { useState, useRef, useContext, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SearchBar from "./SearchBar";
import AppsMenu from "./AppsMenu";
import { SearchContext } from "../../../Context/SearchContext.js";
import "./css/topBar.css";
import "./css/mobile.css";
import "./css/animations.css";
import { FaUser, FaChevronDown } from "react-icons/fa";
import Profile from "../../../Pages/Profiel/index.js";
import useNotification from "../../../Components/Notification";
import useTranslations from "../../../Hooks/useTranslations";
import translations from "./translations.json";
import { useGetAccountQuery } from "../../../store/accountApi.js";
import { useSelector } from "react-redux";
import { selectIsBowlingRestaurant } from "../../../store/setupApi";

const BOWLING_LOGO = "https://static.reservaties.net/images/logo/HappyBowlingFinal.png";

const TopBar = () => {
  const t = useTranslations(translations);
  const menuTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { searchQuery, setSearchQuery } = useContext(SearchContext);
  const isBowling = useSelector(selectIsBowlingRestaurant);

  const [isAppsMenuOpen, setIsAppsMenuOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [restaurantName, setRestaurantName] = useState(
    t.topBar?.defaultRestaurantName || "Restaurant"
  );

  const { triggerNotification, NotificationComponent } = useNotification();

  // ===== Account (single source of truth for display name) =====
  const {
    data: account,
    isLoading: accountLoading,
    isError: accountError,
  } = useGetAccountQuery();

  // Update display name from account → localStorage → translation default
  useEffect(() => {
    if (accountLoading) return;

    const fromAccount =
      (typeof account?.restaurant_name === "string" &&
        account.restaurant_name.trim()) ||
      (typeof account?.restaurantName === "string" &&
        account.restaurantName.trim()) ||
      (typeof account?.company_name === "string" &&
        account.company_name.trim()) ||
      (typeof account?.companyName === "string" &&
        account.companyName.trim()) ||
      (typeof account?.name === "string" && account.name.trim()) ||
      null;

    const fromLS = (() => {
      const v = localStorage.getItem("restaurantName");
      return typeof v === "string" && v.trim().length ? v.trim() : null;
    })();

    const chosen =
      fromAccount || fromLS || t.topBar?.defaultRestaurantName || "Restaurant";

    setRestaurantName(chosen);

    // keep LS in sync if we learned a better value from the API
    if (fromAccount && fromAccount !== fromLS) {
      try {
        localStorage.setItem("restaurantName", fromAccount);
      } catch {}
    }
  }, [account, accountLoading]);

  // Handlers
  const handleAppsMouseEnter = () => {
    clearTimeout(menuTimeoutRef.current);
    setIsAppsMenuOpen(true);
  };
  const handleAppsMouseLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setIsAppsMenuOpen(false);
    }, 50);
  };
  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const openProfileModal = () => setIsProfileModalOpen(true);
  const closeProfileModal = (e) => {
    if (e.target.classList.contains("profile-modal-overlay")) {
      setIsProfileModalOpen(false);
    }
  };

  return (
    <div className="top-bar-component">
      <NotificationComponent />

      <div className="top-bar">
        {/* Left */}
        <div
          className="top-bar-left"
          onMouseEnter={handleAppsMouseEnter}
          onMouseLeave={handleAppsMouseLeave}
        >
          <div
            className={`nine-dots-wrapper ${isAppsMenuOpen ? "active" : ""}`}
          >
            <div className="nine-dots">
              {[...Array(9)].map((_, i) => (
                <span key={i} className={`dot dot-${i + 1}`}></span>
              ))}
            </div>
          </div>
          <a
            href="https://happychef.be"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src={isBowling ? BOWLING_LOGO : "https://static.reservaties.net/images/logo/happychef-logo-color.png"}
              alt={isBowling ? "HappyBowling Logo" : "HappyChef Logo"}
              className="top-bar-logo"
            />
          </a>
        </div>

        {/* Middle */}
        <div className="top-bar-middle">
          {location.pathname === "/day-list" ? (
            <SearchBar
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t.search.placeholder}
            />
          ) : (
            <SearchBar placeholder={t.search.placeholder} />
          )}
        </div>

        {/* Right */}
        <div className="top-bar-right">
          <div className="user-box" onClick={openProfileModal} translate="no">
            <FaUser className="user-icon" />
            <span translate="no" className="user-name">
              {accountLoading ? "…" : restaurantName}
            </span>
            <FaChevronDown className="user-chevron" />
          </div>
        </div>
      </div>

      {/* Apps menu if needed */}
      {/* {isAppsMenuOpen && <AppsMenu />} */}

      {isProfileModalOpen && (
        <div className="profile-modal-overlay" onClick={closeProfileModal}>
          <div className="profile-modal-content">
            <span
              className="profile-modal-close"
              onClick={() => setIsProfileModalOpen(false)}
            >
              &times;
            </span>
            <Profile
              triggerNotification={triggerNotification}
              closeProfile={() => setIsProfileModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TopBar;
